import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: (
    import.meta.env.VITE_API_URL
  ).replace(/\/+$/, ''),
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 seconds timeout
});

// CSRF token management
let csrfToken = null;

// Function to get CSRF token
const getCsrfToken = async () => {
  if (!csrfToken) {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/csrf-token`, {
        withCredentials: true
      });
      csrfToken = response.data.data.csrfToken;
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
    }
  }
  return csrfToken;
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {

    // Add CSRF token to requests (except for GET requests and certain endpoints)
    if (config.method !== 'get' &&
        !config.url?.includes('/api/auth/login') &&
        !config.url?.includes('/api/auth/logout') &&
        !config.url?.includes('/api/auth/refresh-token') &&
        !config.url?.includes('/csrf-token')) {
      try {
        const token = await getCsrfToken();
        if (token) {
          config.headers['X-CSRF-Token'] = token;
        }
      } catch (error) {
        console.error('Failed to add CSRF token:', error);
      }
    }

    // Don't manually add auth header - httpOnly cookies will be sent automatically
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper methods
const apiService = {

  // Clear auth token and data
  clearAuthToken: () => {
    delete api.defaults.headers.common.Authorization;
  },

  // GET request
  get: async (url, config = {}) => {
    const response = await api.get(url, config);
    return response;
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    const response = await api.post(url, data, config);
    return response;
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    const response = await api.put(url, data, config);
    return response;
  },

  // DELETE request
  delete: async (url, config = {}) => {
    const response = await api.delete(url, config);
    return response;
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    const response = await api.patch(url, data, config);
    return response.data;
  },

  // CSRF token management
  refreshCsrfToken: async () => {
    try {
      csrfToken = null; // Clear existing token
      const token = await getCsrfToken();
      return token;
    } catch (error) {
      return null;
    }
  },

  // Get current CSRF token
  getCurrentCsrfToken: () => {
    return csrfToken;
  },

  // Customer sync functions
  // Get customer sync settings
  getCustomerSyncSettings: async () => {
    return await api.get('/customer-sync/settings');
  },

  // Update customer sync settings
  updateCustomerSyncSettings: async (settings) => {
    return await api.put('/customer-sync/settings', settings);
  },

  // Reset customer sync settings to default
  resetCustomerSyncSettings: async () => {
    return await api.post('/customer-sync/settings/reset');
  },

  // Get Shopify to ERP customer sync settings
  getShopifyToErpCustomerSyncSettings: async () => {
    return await api.get('/customer-sync/settings/shopify-to-erp');
  },

  // Update Shopify to ERP customer sync settings
  updateShopifyToErpCustomerSyncSettings: async (settings) => {
    return await api.put('/customer-sync/settings/shopify-to-erp', settings);
  },

  // Reset Shopify to ERP customer sync settings to default
  resetShopifyToErpCustomerSyncSettings: async () => {
    return await api.post('/customer-sync/settings/shopify-to-erp/reset');
  },

  // Get customer field mappings
  getCustomerFieldMappings: async (params = {}) => {
    return await api.get('/customer-sync/field-mappings', { params });
  },

  // Update customer field mappings
  updateCustomerFieldMappings: async (mappings) => {
    return await api.put('/customer-sync/field-mappings', { mappings });
  },

  // Create a new field mapping
  createCustomerFieldMapping: async (mapping) => {
    return await api.post('/customer-sync/field-mappings', mapping);
  },

  // Update a single field mapping
  updateSingleCustomerFieldMapping: async (id, mapping) => {
    return await api.put(`/customer-sync/field-mappings/${id}`, mapping);
  },

  // Delete a specific field mapping
  deleteCustomerFieldMapping: async (id) => {
    return await api.delete(`/customer-sync/field-mappings/${id}`);
  },

  // Reset customer field mappings to default
  resetCustomerFieldMappings: async () => {
    return await api.post('/customer-sync/field-mappings/reset');
  },

  resetCustomerFieldMappingsViaQueue: async () => {
    return await api.post('/customer-sync/field-mappings/reset-queue');
  },

  // Get synced customers with pagination
  getSyncedCustomers: async (params) => {
    return await api.get('/customer-sync/customers', { params });
  },

  // Get synced customers with pagination Direction: Shopify To ERP
  getShopifyToErpSyncedCustomers: async (params) => {
    return await api.get('/customer-sync/customers/shopify-to-erp', { params });
  },

  // Get customer sync status
  getCustomerSyncStatus: async () => {
    return await api.get('/customer-sync/status');
  },

  // Start whole customers sync
  startWholeCustomersSync: async (options) => {
    return await api.post('/customer-sync/sync/whole-customers', options);
  },

  // Start updated customers sync
  startUpdatedCustomersSync: async (options) => {
    return await api.post('/customer-sync/sync/updated-customers', options);
  },

  // Start whole price sync
  startWholePricingSync: async (options) => {
    return await api.post('/pricing-sync/sync/', options);
  },

  // get price sync status
  getPricingSyncStatus: async () => {
    return await api.get('/pricing-sync/status');
  },

  // Start updated price sync
  startUpdatedPricingSync: async (options) => {
    return await api.post('/pricing-sync/sync/', options);
  },

  // Get synced price with pagination
  getSyncedPricing: async (params) => {
    return await api.get('/pricing-sync/pricing-records', { params });
  },

  // Get synced orders with pagination
  getSyncedOrders: async (params) => {
    return await api.get('/order-sync/orders', { params });
  },

  // Get order sync settings
  getOrderSyncSettings: async () => {
    return await api.get('/order-sync/settings');
  },

  // Update order sync settings
  updateOrderSyncSettings: async (settings) => {
    return await api.put('/order-sync/settings', settings);
  },

  // Reset order sync settings to default
  resetOrderSyncSettings: async () => {
    return await api.post('/order-sync/settings/reset');
  },

  getPricingSyncSettings: async () => {
    return await api.get('/pricing-sync/settings');
  },

  updatePricingSyncSettings: async (settings) => {
    return await api.put('/pricing-sync/settings', settings);
  },

  // Reset pricing sync settings to default
  resetPricingSyncSettings: async () => {
    return await api.post('/pricing-sync/settings/reset');
  },

  // Get overall data for dashboard
  getOverallData: async () => {
    return await api.get('/sync-logs/summary');
  },

  // Product Field Mappings functions
  getProductFieldMappings: async () => {
    return await api.get('/product-sync/field-mappings');
  },

  createProductFieldMapping: async (mapping) => {
    return await api.post('/product-sync/field-mappings/single', mapping);
  },

  updateProductFieldMapping: async (id, mapping) => {
    return await api.put(`/product-sync/field-mappings/${id}`, mapping);
  },

  deleteProductFieldMapping: async (id) => {
    return await api.delete(`/product-sync/field-mappings/${id}`);
  },

  resetProductFieldMappings: async () => {
    return await api.post('/product-sync/field-mappings/reset');
  },

  resetProductFieldMappingsViaQueue: async () => {
    return await api.post('/product-sync/field-mappings/reset-queue');
  },

  // Get product mappings with pagination
  getProductMappings: async (params) => {
    return await api.get('/product-sync/product-mappings', { params });
  },

  // Tracking Sync functions
  getTrackingSyncSettings: async () => {
    return await api.get('/tracking-sync/settings');
  },

  updateTrackingSyncSettings: async (settings) => {
    return await api.put('/tracking-sync/settings', settings);
  },

  resetTrackingSyncSettings: async () => {
    return await api.post('/tracking-sync/settings/reset');
  },

  getTrackingFieldMappings: async (params) => {
    return await api.get('/tracking-sync/field-mappings', { params });
  },

  updateTrackingFieldMapping: async (id, mapping) => {
    return await api.put(`/tracking-sync/field-mappings/${id}`, mapping);
  },

  createTrackingFieldMapping: async (mapping) => {
    return await api.post('/tracking-sync/field-mappings', mapping);
  },

  deleteTrackingFieldMapping: async (id) => {
    return await api.delete(`/tracking-sync/field-mappings/${id}`);
  },

  resetTrackingFieldMappings: async () => {
    return await api.post('/tracking-sync/field-mappings/reset');
  },

  resetTrackingFieldMappingsViaQueue: async () => {
    return await api.post('/tracking-sync/field-mappings/reset-queue');
  }
};

// Product sync functions
export const getProductSyncSettings = async () => {
  return await api.get('/product-sync/settings');
};

export const updateProductSyncSettings = async (settings) => {
  return await api.put('/product-sync/settings', settings);
};

export const resetProductSyncSettings = async () => {
  return await api.post('/product-sync/settings/reset');
};

export const getProductSyncStatus = async () => {
  return await api.get('/product-sync/status');
};

export const syncProductsWhole = async () => {
  return await api.post('/product-sync/sync/whole-items');
};

export const syncProductsToday = async () => {
  return await api.post('/product-sync/sync/updated-items');
};

// Sync logs endpoints
export const getSyncLogs = async (params) => {
  return apiService.get('/sync-logs', { params });
};

export const getSyncLogById = async (id) => {
  return apiService.get(`/sync-logs/${id}`);
};

// Get sync log with detailed data from both tables
export const getSyncLogWithData = async (id, params = {}) => {
  return apiService.get(`/sync-logs/${id}/data`, { params });
};

// User transfer methods
export const checkUserReferences = async (userId) => {
  try {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 409) {
      // User has references, return the conflict data
      return error.response.data;
    }
    throw error;
  }
};

export const transferUserDataAndDelete = async (userId, transferToUserId) => {
  const response = await api.post(`/users/${userId}/transfer-and-delete`, {
    transferToUserId
  });
  return response.data;
};

export const getTransferOptions = async (excludeUserId) => {
  const response = await api.get(`/users/transfer-options/${excludeUserId}`);
  return response.data.data; // Return the nested data array
};

// Webhook Config methods
export const getWebhookConfigs = async (params = {}) => {
  return apiService.get('/webhook-config', { params });
};

export const getActiveWebhookConfigByTopic = async (topic, shopifyConfigId) => {
  return apiService.get(`/webhook-config/topic/${topic}/shopify-config/${shopifyConfigId}`);
};

export const checkWebhookStatusInShopify = async (shopifyConfigId) => {
  return apiService.post(`/webhook-config/shopify-config/${shopifyConfigId}/check-status`);
};

export const checkActiveWebhookByTopic = async (shopifyConfigId, topic) => {
  return api.get(`/webhook-config/shopify-config/${shopifyConfigId}/active/${topic}`);
};

export default apiService;
